import * as History from 'history'
import { CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'

import { FontAwesomeIconName, LegacyIconName } from 'components/Icon'
import { usePaymentSetup } from 'components/payment/use-payment-setup'
import useAuth from 'hooks/use-auth'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { trans } from 'i18n/translate'
import { hasSupportAppPermission } from 'support/helpers/utilities'
import { UserSessionInfo } from 'types/api/user/session'

export interface navItemConfig {
  isActive: boolean
  name: string
  disabled: boolean
  icon: FontAwesomeIconName | LegacyIconName
  text: string
  route: History.LocationDescriptor
  props?: Record<string, any>
  showNotification?: boolean
  enabled: boolean
  showSeparator?: boolean
  style?: CSSProperties
}

export interface useNavigationHook {
  navigationConfigItems: navItemConfig[]
}

export function useNavigation({
  hasFaultInjection,
  hasReliabilityManagement,
  isAdmin,
  isFreeEdition,
  pendingRequestsByAssetId,
  session,
  userHasTeams
}: {
  hasFaultInjection: boolean
  hasReliabilityManagement: boolean
  isAdmin: boolean
  isFreeEdition: boolean
  // TODO: When utils.js is converted to typescript and the function responses are type, need to add that here.
  pendingRequestsByAssetId: Record<string, any>
  session: UserSessionInfo
  userHasTeams: boolean
}): useNavigationHook {
  const {
    createGamedays: isCreateGameDaysEnabled,
    sharedHealthChecks: isSharedHealthChecksEnabled
  } = useFeatureFlags()

  const { pathname: currentPathname } = useLocation()
  const disableForOnboarding =
    currentPathname.startsWith('/onboarding') &&
    currentPathname !== '/onboarding/profile'

  const isOnboardingOrHasNoTeams = disableForOnboarding || !userHasTeams

  const { isTrial } = useAuth()
  const { handleCheckout, isSubmitting: isSubmittingCheckout } =
    usePaymentSetup()

  const navigationConfigItems: navItemConfig[] = [
    {
      isActive:
        currentPathname.startsWith('/dashboard') || currentPathname === '/',
      name: 'dashboard',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'grid2',
      text: 'Dashboard',
      route: '/dashboard',
      enabled: isFreeEdition
    },
    {
      isActive:
        currentPathname.startsWith('/dashboard') || currentPathname === '/',
      name: 'dashboard',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'grid2',
      text: 'Dashboard',
      route: '/dashboard',
      enabled: isFreeEdition
    },
    {
      isActive: currentPathname.startsWith('/dashboard'),
      name: 'dashboard',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartLineUp',
      text: 'Dashboard',
      route: '/dashboard',
      props: { 'data-test-nav-company-dashboard': true },
      enabled: hasReliabilityManagement
    },
    {
      isActive: currentPathname.startsWith('/services'),
      name: 'services',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartNetwork',
      text: 'Services',
      route: {
        pathname: '/services',
        state: {
          clearState: true
        }
      },
      props: { 'data-test-nav-services': true },
      enabled: hasReliabilityManagement
    },
    {
      isActive: currentPathname.startsWith('/health-checks'),
      name: 'status-checks',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'wavePulse',
      text: trans('status_checks.label.plural'),
      route: {
        pathname: '/health-checks/list'
      },
      props: { 'data-test-nav-status-checks': true },
      enabled: isSharedHealthChecksEnabled
    },
    {
      isActive: currentPathname.startsWith('/getting-started'),
      name: 'getting-started',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'graduationCap',
      text: 'Getting Started',
      route: {
        pathname: '/getting-started'
      },
      props: { 'data-test-nav-getting-started': true },
      enabled: hasReliabilityManagement
    },
    {
      isActive: currentPathname.startsWith('/scenarios'),
      name: 'scenarios',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'clipboardList',
      text: 'Scenarios',
      route: {
        pathname: '/scenarios/my-scenarios',
        state: {
          clearSearchState: true
        }
      },
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/gamedays'),
      name: 'gamedays',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'joystick',
      text: 'GameDays',
      route: '/gamedays',
      enabled: isCreateGameDaysEnabled && hasFaultInjection
    },
    {
      isActive:
        currentPathname.startsWith('/attacks') ||
        currentPathname.startsWith('/alfi'),
      name: 'attacks',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'bolt',
      text: 'Attacks',
      route: '/attacks/infrastructure',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/schedules'),
      name: 'schedules',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'calendar',
      text: 'Schedules',
      route: '/schedules',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/clients'),
      name: 'clients',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'clients',
      text: 'Agents',
      route: '/clients',
      showNotification: Object.keys(pendingRequestsByAssetId).length > 0,
      enabled: true
    },
    {
      isActive: currentPathname.startsWith('/reports/'),
      name: 'reports',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartPieSimple',
      text: 'Reports',
      route: '/reports/company',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname === '/admin/reports',
      name: 'admin-reports',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartPieSimple',
      text: 'Admin Reports',
      route: '/admin/reports',
      enabled: isAdmin
    },
    {
      isActive: currentPathname === '/help',
      name: 'help',
      disabled: false,
      icon: 'messageQuestion',
      text: 'Help',
      route: '/help',
      enabled: true,
      showSeparator: true
    },
    {
      isActive:
        currentPathname.startsWith('/support/') ||
        currentPathname.startsWith('/supportnew/'),
      name: 'support',
      disabled: false,
      icon: 'support',
      text: 'Support App',
      route: '/support',
      enabled: hasSupportAppPermission(session)
    },
    {
      isActive: false,
      name: 'upgrade',
      disabled: isSubmittingCheckout,
      icon: 'solidCreditCard',
      text: 'Upgrade Now',
      route: '#',
      enabled: hasReliabilityManagement && isTrial,
      style: {
        // align this button to the bottom
        marginTop: 'auto'
      },
      props: {
        onClick: () => {
          if (!isSubmittingCheckout) {
            handleCheckout()
          }
        }
      }
    }
  ]

  return {
    navigationConfigItems
  }
}
